/**
 * API Service for Integration Framework
 */
import axios from 'axios';
import { ExternalSystem, SystemConnection, SyncConfiguration, SyncLog, DataDiscrepancy, SystemHealth } from '../types/integration';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const INTEGRATION_API = `${API_BASE_URL}/integration`;

// Get auth token from localStorage
const getAuthToken = () => localStorage.getItem('token');

// Create axios instance with auth
const api = axios.create({
    baseURL: INTEGRATION_API,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
        config.headers.Authorization = `Token ${token}`;
    }
    return config;
});

export const integrationService = {
    // External Systems
    getSystems: async (): Promise<ExternalSystem[]> => {
        const response = await api.get('/systems/');
        // Handle both paginated and non-paginated responses
        return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },

    getSystem: async (id: number): Promise<ExternalSystem> => {
        const response = await api.get(`/systems/${id}/`);
        return response.data;
    },

    createSystem: async (data: Partial<ExternalSystem>): Promise<ExternalSystem> => {
        const response = await api.post('/systems/', data);
        return response.data;
    },

    updateSystem: async (id: number, data: Partial<ExternalSystem>): Promise<ExternalSystem> => {
        const response = await api.put(`/systems/${id}/`, data);
        return response.data;
    },

    deleteSystem: async (id: number): Promise<void> => {
        await api.delete(`/systems/${id}/`);
    },

    testConnection: async (id: number): Promise<{ success: boolean; message: string; tested_at: string }> => {
        const response = await api.post(`/systems/${id}/test_connection/`);
        return response.data;
    },

    triggerSync: async (id: number, syncConfigId?: number): Promise<{ message: string; sync_log_id: number; status: string }> => {
        const response = await api.post(`/systems/${id}/sync/`, {
            sync_config_id: syncConfigId,
            trigger_type: 'manual',
        });
        return response.data;
    },

    getSyncHistory: async (id: number, limit: number = 20): Promise<SyncLog[]> => {
        const response = await api.get(`/systems/${id}/sync_history/`, {
            params: { limit },
        });
        return response.data;
    },

    // System Connections
    getConnections: async (systemId?: number): Promise<SystemConnection[]> => {
        const response = await api.get('/connections/', {
            params: systemId ? { external_system: systemId } : {},
        });
        return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },

    createConnection: async (data: Partial<SystemConnection>): Promise<SystemConnection> => {
        const response = await api.post('/connections/', data);
        return response.data;
    },

    updateConnection: async (id: number, data: Partial<SystemConnection>): Promise<SystemConnection> => {
        const response = await api.put(`/connections/${id}/`, data);
        return response.data;
    },

    deleteConnection: async (id: number): Promise<void> => {
        await api.delete(`/connections/${id}/`);
    },

    // Sync Configurations
    getSyncConfigs: async (systemId?: number): Promise<SyncConfiguration[]> => {
        const response = await api.get('/sync-configs/', {
            params: systemId ? { external_system: systemId } : {},
        });
        return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },

    createSyncConfig: async (data: Partial<SyncConfiguration>): Promise<SyncConfiguration> => {
        const response = await api.post('/sync-configs/', data);
        return response.data;
    },

    updateSyncConfig: async (id: number, data: Partial<SyncConfiguration>): Promise<SyncConfiguration> => {
        const response = await api.put(`/sync-configs/${id}/`, data);
        return response.data;
    },

    deleteSyncConfig: async (id: number): Promise<void> => {
        await api.delete(`/sync-configs/${id}/`);
    },

    // Sync Logs
    getSyncLogs: async (systemId?: number): Promise<SyncLog[]> => {
        const response = await api.get('/sync-logs/', {
            params: systemId ? { external_system: systemId } : {},
        });
        return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },

    getSyncLog: async (id: number): Promise<SyncLog> => {
        const response = await api.get(`/sync-logs/${id}/`);
        return response.data;
    },

    // Data Discrepancies
    getDiscrepancies: async (systemId?: number, resolutionStatus?: string): Promise<DataDiscrepancy[]> => {
        const response = await api.get('/discrepancies/', {
            params: {
                ...(systemId && { external_system: systemId }),
                ...(resolutionStatus && { resolution_status: resolutionStatus }),
            },
        });
        return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },

    resolveDiscrepancy: async (id: number, notes: string): Promise<DataDiscrepancy> => {
        const response = await api.post(`/discrepancies/${id}/resolve/`, {
            resolution_notes: notes,
        });
        return response.data;
    },

    // System Health
    getSystemHealth: async (): Promise<SystemHealth[]> => {
        const response = await api.get('/health/');
        return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },
};
